import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Icon } from '@nula/core-ui';
import { RecordingDot } from '@/components/recording-dot/recording-dot';
import type { OrbStatus } from '../utils/derive-orb-status';
import styles from './surface-orb.module.css';


interface SurfaceOrbButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'type'> {
  count: number;
  displayCount: number;
  open: boolean;
  status?: OrbStatus;
}

const orb = cva(styles.orb, {
  variants: {
    open: { true: styles.open },
    status: {
      idle: undefined,
      generating: styles.ringGenerating,
      failed: styles.ringFailed,
      recording: undefined,
    },
  },
});


const STATUS_SUFFIX: Record<OrbStatus, string> = {
  idle: '',
  generating: ', generating',
  failed: ', failed',
  recording: ', recording',
};

/**
 * Two overlapping sheets — several surfaces, one of them in front. Drawn on the
 * Material Symbols grid so it sits beside the panel's real icons, and strokes
 * `currentColor` so the open state's colour flip is one rule on the button.
 *
 * Open swaps to the same two axes at ±45°: the close state is this shape rotated,
 * not a different icon cross-faded in. See `ui-design.md` §1.0.
 */
function SurfaceStackGlyph({ open }: { open: boolean }) {
  return (
    <svg
      className={styles.glyph}
      viewBox="0 -960 960 960"
      aria-hidden="true"
      focusable="false"
    >
      {open ? (
        <>
          <path className={styles.glyphStroke} d="M290 -670 L670 -290" />
          <path className={styles.glyphStroke} d="M670 -670 L290 -290" />
        </>
      ) : (
        <>
          <rect className={styles.glyphRear} x="185" y="-775" width="480" height="480" rx="64" />
          <rect className={styles.glyphStroke} x="295" y="-665" width="480" height="480" rx="64" />
        </>
      )}
    </svg>
  );
}

function accessibleName(seed: boolean, displayCount: number, status: OrbStatus): string {
  if (seed) return 'Add a surface';
  return `Surfaces, ${displayCount}${STATUS_SUFFIX[status]}`;
}

/**
 * The 52px floating control. `SurfaceMount` is what the rest of the app calls
 * "the orb" — this is the button alone.
 *
 * Hand-written, not composed on `IconButton`: `IconButton`'s `.iconBase` sets
 * `opacity: var(--opacity-muted)` (washes out the glass), `overflow: hidden`
 * (clips the recording dot, which sits at `top: -3px; right: -3px`), and
 * `.iconBase:hover:not(:disabled) { background: ... }` at (0,3,0) specificity
 * against this component's own (0,1,0) `.orb` rule — the hover background
 * would win regardless of source order. `.iconMd` is also 32px against the
 * orb's 52px. None of these are overridable from the outside without fighting
 * IconButton's own rules, so the box is a plain `<button>` — no core-ui
 * primitive gives an unstyled, arbitrarily-sized circular button.
 *
 * Forwards its ref to the underlying button: `SurfaceMount` renders this as
 * `Popover`'s direct trigger child, and `Popover` clones a ref onto it to
 * measure the trigger's position (`useFloating`). Without ref-forwarding the
 * measurement silently no-ops and the panel never leaves its unmeasured
 * off-screen default — not a jsdom artifact, the same in a real browser.
 *
 * No `onToggle` prop: `SurfaceMount` renders this as `Popover`'s trigger with
 * `trigger="click"`, and `Popover` clones its own `onClick` onto this element
 * (`Popover.tsx`) via `React.cloneElement`, which lands in `...props` below.
 * An `onToggle` prop existed until this component's own tests exposed that it
 * never fired in the app (the clone replaced it) — removed rather than kept
 * dead. The button must still spread `...props` for the same clone to reach
 * the DOM element at all.
 */
export const SurfaceOrbButton = forwardRef<HTMLButtonElement, SurfaceOrbButtonProps>(function SurfaceOrbButton(
  { count, displayCount, open, status = 'idle', className, ...props },
  ref,
) {
  // The seed state is about manifest entries (zero of them means nothing to
  // switch to yet), while displayCount is the body-inclusive number the user
  // sees — they diverge on purpose and must not be reconciled.
  const seed = count === 0;

  return (
    <button
      ref={ref}
      type="button"
      aria-label={accessibleName(seed, displayCount, status)}
      aria-expanded={open}
      aria-haspopup="menu"
      className={clsx(orb({ open, status }), seed && styles.seed, className)}
      {...props}
    >
      {seed ? (
        <Icon name="add" size={28} className={styles.seedIcon} />
      ) : (
        <SurfaceStackGlyph open={open} />
      )}
      {status === 'recording' && <RecordingDot className={styles.recordingBadge} />}
    </button>
  );
});
